// Compact NFC chip payload for NTAG213 (144 bytes user memory).
// Effective NDEF text record overhead ~10 bytes, so target ≤130 bytes for the JSON.
// Keys are kept short on purpose. The schema is versioned via `v`.

export type ChipEntityType = 'plotter_cut' | 'mold' | 'piece' | 'autoclave';
export type ChipStatus = 'in_progress' | 'completed' | 'paused' | 'idle';

export interface ChipPayload {
  v: 1;
  wo?: string;          // order_number (legible)
  ent?: ChipEntityType;
  ent_id?: string;      // last 8 chars of UUID (compact)
  step?: string;        // process code or short name (max 25 chars)
  step_seq?: number;    // sequence_order (1-14)
  status?: ChipStatus;
  op?: string;          // operator name (truncated to 30)
  at?: string;          // ISO short "YYYY-MM-DDTHH:MM"
  cycle?: string;       // autoclave cycle_number when relevant
}

export const NTAG213_MAX_PAYLOAD = 130;

const compactDate = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  // Keep "YYYY-MM-DDTHH:MM" — 16 chars
  return iso.slice(0, 16);
};

const truncate = (s: string | undefined, n: number): string | undefined => {
  if (!s) return s;
  return s.length > n ? s.slice(0, n) : s;
};

const shortId = (uuid?: string): string | undefined => {
  if (!uuid) return undefined;
  // Take last 8 chars of UUID (still unique enough for chip-side reference).
  return uuid.slice(-8);
};

export const serializePayload = (p: ChipPayload): string => JSON.stringify(p);

export const deserializePayload = (raw: string): ChipPayload | null => {
  try {
    const obj = JSON.parse(raw);
    if (obj && obj.v === 1) return obj as ChipPayload;
    return null;
  } catch {
    return null;
  }
};

export const estimateSize = (p: ChipPayload): number => {
  return new TextEncoder().encode(serializePayload(p)).length;
};

/**
 * Builds a payload that fits in NTAG213 by progressively trimming optional fields.
 * Order of trimming: cycle → op → step (to seq+code) → keep mandatory minimum.
 */
export const fitNtag213 = (input: ChipPayload): ChipPayload => {
  const p: ChipPayload = {
    ...input,
    at: compactDate(input.at),
    op: truncate(input.op, 30),
    step: truncate(input.step, 25),
    ent_id: input.ent_id && input.ent_id.length > 8 ? shortId(input.ent_id) : input.ent_id,
  };
  if (estimateSize(p) <= NTAG213_MAX_PAYLOAD) return p;

  // Trim 1: shorter op
  if (p.op && p.op.length > 20) p.op = p.op.slice(0, 20);
  if (estimateSize(p) <= NTAG213_MAX_PAYLOAD) return p;

  // Trim 2: drop cycle if present and not strictly needed
  if (p.cycle) delete p.cycle;
  if (estimateSize(p) <= NTAG213_MAX_PAYLOAD) return p;

  // Trim 3: shorter step (keep first 12 chars)
  if (p.step && p.step.length > 12) p.step = p.step.slice(0, 12);
  if (estimateSize(p) <= NTAG213_MAX_PAYLOAD) return p;

  // Trim 4: drop op
  delete p.op;
  if (estimateSize(p) <= NTAG213_MAX_PAYLOAD) return p;

  // Trim 5: drop wo (keep ent_id which is enough for server lookup)
  delete p.wo;
  return p;
};

export const buildPayload = (input: Partial<ChipPayload>): ChipPayload => {
  const base: ChipPayload = { v: 1, ...input } as ChipPayload;
  return fitNtag213(base);
};
